import type { BufferGeometry } from "three";

export type BoardAxis = 0 | 1 | 2;

/** Shelves run along X; upright boards and facade faces run along Y. */
export function boardGrainAxis(spans: readonly number[]): BoardAxis {
  const thicknessAxis = spans.indexOf(Math.min(...spans));
  return thicknessAxis === 1 ? 0 : 1;
}

/** Physical UVs with one grain direction for the entire board, including short upright segments. */
export function applyBoardTextureUvs(geometry: BufferGeometry, acrossMeters: number, alongMeters: number, orientGrain: boolean, offsetU = 0, offsetV = 0, grainAxis?: BoardAxis) {
  const position = geometry.getAttribute("position");
  const normal = geometry.getAttribute("normal");
  const uv = geometry.getAttribute("uv");
  if (!position || !normal || !uv) return;
  geometry.computeBoundingBox();
  const bounds = geometry.boundingBox!;
  const spans = [bounds.max.x - bounds.min.x, bounds.max.y - bounds.min.y, bounds.max.z - bounds.min.z];
  const across = Math.max(0.001, acrossMeters);
  const along = Math.max(0.001, alongMeters);
  const grain = grainAxis ?? boardGrainAxis(spans);
  for (let index = 0; index < position.count; index += 1) {
    const nx = Math.abs(normal.getX(index));
    const ny = Math.abs(normal.getY(index));
    const nz = Math.abs(normal.getZ(index));
    // Resolve ties once: bevel normals can be exactly halfway between faces.
    let a: number, b: number;
    if (nx >= ny && nx >= nz) { a = 2; b = 1; }
    else if (ny >= nx && ny >= nz) { a = 0; b = 2; }
    else { a = 0; b = 1; }
    if (orientGrain && a === grain) [a, b] = [b, a];
    // The edge band has a separate material. Never squeeze four complete
    // photo samples into an 18 mm edge: that creates stripes and aliasing.
    uv.setXY(index, position.getComponent(index, a) / across + offsetU, position.getComponent(index, b) / along + offsetV);
  }
  uv.needsUpdate = true;
}

/** Stable per-board cuts; neither movement nor a scene rebuild changes the sample. */
export function boardTextureOffset(identity: string) {
  const hash = (salt: string) => {
    let value = 2166136261;
    for (const character of identity + salt) value = Math.imul(value ^ character.charCodeAt(0), 16777619);
    value ^= value >>> 16; value = Math.imul(value, 0x7feb352d);
    value ^= value >>> 15; value = Math.imul(value, 0x846ca68b);
    return ((value ^ (value >>> 16)) >>> 0) / 4294967296;
  };
  return { u: hash(":across"), v: hash(":along") };
}

/** Match opposite borders while preserving the image interior and avoiding mirrored knots. */
export function tileableDecorPixels(source: Uint8ClampedArray, width: number, height: number) {
  if (width < 1 || height < 1 || source.length !== width * height * 4) throw new Error("Invalid decor pixel dimensions");
  let result = source.slice();
  for (const axis of [0, 1]) {
    const input = result;
    result = input.slice();
    const span = axis === 0 ? width : height;
    const feather = Math.max(1, Math.floor(span * 0.07));
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const distance = Math.min(axis === 0 ? x : y, span - 1 - (axis === 0 ? x : y));
        if (distance >= feather) continue;
        const t = distance / feather;
        const weight = (1 - t * t * (3 - 2 * t)) * 0.5;
        const index = (y * width + x) * 4;
        const opposite = (axis === 0 ? y * width + width - 1 - x : (height - 1 - y) * width + x) * 4;
        for (let channel = 0; channel < 3; channel += 1) {
          result[index + channel] = input[index + channel]! * (1 - weight) + input[opposite + channel]! * weight;
        }
      }
    }
  }
  return result;
}

/** Dark patches in a decor are pigment, not dents: keep only fine pore relief. */
export function decorDetailPixels(source: Uint8ClampedArray, width: number, height: number) {
  if (width < 1 || height < 1 || source.length !== width * height * 4) throw new Error("Invalid decor pixel dimensions");
  const luminance = new Float32Array(width * height);
  for (let i = 0; i < luminance.length; i += 1) {
    luminance[i] = source[i * 4]! * 0.2126 + source[i * 4 + 1]! * 0.7152 + source[i * 4 + 2]! * 0.0722;
  }
  const relief = new Uint8ClampedArray(source.length);
  const roughness = new Uint8ClampedArray(source.length);
  const sample = (x: number, y: number) => luminance[((y + height) % height) * width + (x + width) % width]!;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const localMean = (sample(x - 1, y) + sample(x + 1, y) + sample(x, y - 1) + sample(x, y + 1)) / 4;
      const detail = Math.max(-24, Math.min(24, (sample(x, y) - localMean) * 0.65));
      const index = (y * width + x) * 4;
      for (let channel = 0; channel < 3; channel += 1) {
        relief[index + channel] = 128 + detail;
        roughness[index + channel] = 240 - detail * 0.2;
      }
      relief[index + 3] = roughness[index + 3] = 255;
    }
  }
  return { relief, roughness };
}
